import {useEffect, useMemo} from "react";
import {useDispatch, useSelector} from "react-redux";
import {useNavigate, useParams, useSearchParams} from "react-router-dom";
import Categories from "../components/categories";
import Results from "../components/results";
import {getNotFoundPageInternalUrl} from "../constants/appRoutes";
import {USER_ERRORS_NOT_ACCESSIBLE_DATASET} from "../translations/getManagedErrorsTranslations";
import ViewersDomain from "./ViewersDomain";
import {baseUrlSelector} from "../state/config/configSelectors";
import {showUserLoginForm} from "../state/user/userActions";
import {userSelector} from "../state/user/userSelectors";
import {getCategoryFromCatalog} from "../utils/catalog";
import {getIsUserAuthenticated} from "../utils/permissions";

const CategoriesDomain = ({hub, node, catalog, nodeCode, isDefault}) => {
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const {"*": params} = useParams();
  const [searchParams] = useSearchParams();

  const baseURL = useSelector(baseUrlSelector);

  const fullPath = params.endsWith("/") ? params.slice(0, -1).split("/") : params.split("/");

  const isAccessible = !!searchParams.get("accessible");

  const isCategoriesPage = useMemo(() => params.length === 0, [params]);

  const isCategoryPage = useMemo(() => getCategoryFromCatalog(fullPath, catalog) !== null, [fullPath, catalog]);

  const isUncategorizedPackagesPage = useMemo(
    () => fullPath.length === 1 && fullPath[0] === "uncategorized",
    [fullPath]
  );

  const isPackagePage = useMemo(() => {
    if (fullPath.length < 2) {
      return false;
    }
    const packageId = fullPath[fullPath.length - 1];
    return catalog.packageMap[packageId] !== undefined;
  }, [fullPath, catalog]);

  const user = useSelector(userSelector);
  useEffect(() => {
    if (catalog.isEmpty || !(isCategoriesPage || isCategoryPage || isUncategorizedPackagesPage || isPackagePage)) {
      if (!getIsUserAuthenticated(user)) {
        if (!user.isLoginDialogOpen) {
          dispatch(showUserLoginForm(USER_ERRORS_NOT_ACCESSIBLE_DATASET, "warning", true));
        }
      } else {
        navigate(getNotFoundPageInternalUrl());
      }
    }
  }, [
    catalog,
    isCategoriesPage,
    isCategoryPage,
    isUncategorizedPackagesPage,
    isPackagePage,
    nodeCode,
    user,
    dispatch,
    navigate
  ]);

  if (catalog.isEmpty) {
    /* empty catalog, nothing to show */
    return null;
  } else if (isCategoriesPage) {
    /* list of category schemes, or the top-level category if the catalog has only one category scheme */
    return <Categories hub={hub} node={node} catalog={catalog} nodeCode={nodeCode} isDefault={isDefault} />;
  } else if (isCategoryPage || isUncategorizedPackagesPage) {
    /* list of child categories and packages of the category, or uncategorized packages */
    const category = getCategoryFromCatalog(fullPath, catalog);

    const packages = category
      ? category.packageIdentifiers.map(id => ({
          ...catalog.packageMap[id],
          identifier: id,
          categoryPath: fullPath
        }))
      : catalog.uncategorizedPackages.map(id => ({
          ...catalog.packageMap[id],
          identifier: id,
          categoryPath: ["uncategorized"]
        }));

    const subCategories = category ? category.childrenCategories : [];

    return (
      <Results
        hub={hub}
        node={node}
        nodeCode={nodeCode}
        isDefault={isDefault}
        catalog={catalog}
        categoryPath={fullPath}
        datasets={packages}
        subCategories={subCategories.map(c => ({
          ...c,
          image: c.image ? baseURL + c.image : undefined
        }))}
        isAccessible={isAccessible}
      />
    );
  } else if (isPackagePage) {
    /* package viewer */
    const packageId = fullPath[fullPath.length - 1];
    return (
      <ViewersDomain
        hub={hub}
        node={node}
        nodeCode={nodeCode}
        isDefault={isDefault}
        catalog={catalog}
        categoryPath={fullPath.slice(0, fullPath.length - 1)}
        packageId={packageId}
      />
    );
  } else {
    /* wrong url, nothing to show */
    return null;
  }
};

export default CategoriesDomain;
